import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import agentApiService from '../../services/agentApiService';

const CustomerBookingDetail = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [showRebookModal, setShowRebookModal] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketForm, setTicketForm] = useState({
    queryType: 'BOOKING',
    query: ''
  });

  useEffect(() => {
    if (bookingId) {
      fetchBookingDetails();
    }
  }, [bookingId]);

  const fetchBookingDetails = async () => {
    try {
      setLoading(true);
      const response = await agentApiService.getCustomerBookingDetail(bookingId);
      
      if (response.success) {
        setBookingData(response);
      } else {
        toast.error(response.message || 'Failed to load booking details');
      }
    } catch (error) {
      console.error('Error fetching booking details:', error);
      toast.error('Failed to load booking details: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toUpperCase()) {
      case 'BOOKED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'HOLD':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'PENDING':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handleTicketSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await agentApiService.createSupportTicket(bookingId, ticketForm);
      if (response.success) {
        toast.success('Support ticket submitted successfully');
        setShowTicketModal(false);
        setTicketForm({ queryType: 'BOOKING', query: '' });
      } else {
        toast.error(response.message || 'Failed to submit ticket');
      }
    } catch (error) {
      console.error('Error submitting ticket:', error);
      toast.error('Failed to submit ticket: ' + error.message);
    }
  };

  const handleCancel = async () => {
    try {
      const response = await agentApiService.cancelBooking(bookingId);
      if (response.success) {
        toast.success('Booking cancellation initiated successfully');
        setShowCancelModal(false);
        fetchBookingDetails(); // Refresh data
      } else {
        toast.error(response.message || 'Failed to cancel booking');
      }
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error('Failed to cancel booking: ' + error.message);
    }
  };

  const handleVoucher = async () => {
    try {
      const response = await agentApiService.generateVoucher(bookingId);
      if (response.success) {
        toast.success('Voucher generated successfully');
        setShowVoucherModal(false);
      } else {
        toast.error(response.message || 'Failed to generate voucher');
      }
    } catch (error) {
      console.error('Error generating voucher:', error);
      toast.error('Failed to generate voucher: ' + error.message);
    }
  };

  const handleRebook = () => {
    // Navigate to rebooking page
    window.open(`/re-booking/${bookingId}`, '_blank');
    setShowRebookModal(false);
  };

  const handleGeneratePDF = async () => {
    try {
      const response = await agentApiService.generateBookingPDF(bookingId);
      if (response.success) {
        toast.success('PDF generated and downloaded successfully');
      } else {
        toast.error('Failed to generate PDF');
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!bookingData || !bookingData.bookingDetails) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-exclamation-triangle text-4xl text-red-500 mb-4"></i>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Booking Not Found</h2>
          <p className="text-gray-600 mb-4">The booking you're looking for doesn't exist or you don't have access to it.</p>
          <button
            onClick={() => navigate('/agent/dashboard/bookingdetails')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            Back to Bookings
          </button>
        </div>
      </div>
    );
  }

  const { bookingDetails } = bookingData;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/agent/dashboard/bookingdetails')}
                className="text-blue-600 hover:text-blue-800 transition-colors duration-200"
              >
                <i className="fas fa-arrow-left text-xl"></i>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                  <i className="fas fa-file-alt text-blue-600 mr-3"></i>
                  Booking Details
                </h1>
                <p className="mt-2 text-gray-600">Complete information for booking #{bookingId}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowTicketModal(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center"
              >
                <i className="fas fa-ticket-alt mr-2"></i>
                Add Ticket
              </button>
              <span className={`px-4 py-2 rounded-full text-sm font-semibold border ${getStatusBadgeClass(bookingDetails.status)}`}>
                {bookingDetails.status || 'N/A'}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Hotel Information */}
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg">
                <h2 className="text-lg font-semibold flex items-center">
                  <i className="fas fa-hotel mr-2"></i>
                  Hotel Information
                </h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="space-y-4">
                    {bookingDetails.hotelName && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 uppercase tracking-wider">Property Name</label>
                        <p className="mt-1 text-lg font-semibold text-gray-900">{bookingDetails.hotelName}</p>
                      </div>
                    )}
                    {bookingDetails.hotelAddress && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 uppercase tracking-wider">Address</label>
                        <p className="mt-1 text-gray-900">{bookingDetails.hotelAddress}</p>
                      </div>
                    )}
                    {bookingDetails.hotelStar > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 uppercase tracking-wider">Rating</label>
                        <p className="mt-1 text-gray-900">{bookingDetails.hotelStar} Star</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-4">
                    {bookingDetails.bookingId && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 uppercase tracking-wider">Booking ID</label>
                        <p className="mt-1 text-lg font-mono font-semibold text-blue-600">{bookingDetails.bookingId}</p>
                      </div>
                    )}
                    {bookingDetails.confirmationNo && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 uppercase tracking-wider">Confirmation ID</label>
                        <p className="mt-1 text-gray-900">{bookingDetails.confirmationNo}</p>
                      </div>
                    )}
                    {bookingDetails.hotelConfirmationNo && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 uppercase tracking-wider">Hotel Confirmation</label>
                        <p className="mt-1 text-gray-900">{bookingDetails.hotelConfirmationNo}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-4">
                    {bookingDetails.checkInDate && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 uppercase tracking-wider">Check-in Date</label>
                        <p className="mt-1 text-gray-900 flex items-center">
                          <i className="fas fa-calendar-plus text-green-600 mr-2"></i>
                          {formatDate(bookingDetails.checkInDate)}
                        </p>
                      </div>
                    )}
                    {bookingDetails.checkOutDate && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 uppercase tracking-wider">Check-out Date</label>
                        <p className="mt-1 text-gray-900 flex items-center">
                          <i className="fas fa-calendar-minus text-red-600 mr-2"></i>
                          {formatDate(bookingDetails.checkOutDate)}
                        </p>
                      </div>
                    )}
                    {bookingDetails.nightDuration && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 uppercase tracking-wider">Duration</label>
                        <p className="mt-1 text-gray-900">{bookingDetails.nightDuration} Nights</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Amenities */}
                {bookingDetails.roomsInfoResponseDtos?.[0]?.roomAmenities && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Room Amenities</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {bookingDetails.roomsInfoResponseDtos[0].roomAmenities.map((amenity, index) => (
                        <div key={index} className="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-lg">
                          <i className="fas fa-check text-green-600"></i>
                          <span className="text-sm text-gray-700">{amenity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Room Allocation & Passenger Details */}
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="px-6 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-t-lg">
                <h2 className="text-lg font-semibold flex items-center">
                  <i className="fas fa-users mr-2"></i>
                  Room Allocation & Passenger Details
                </h2>
              </div>
              <div className="p-6">
                {bookingDetails.roomsInfoResponseDtos?.map((room, roomIndex) => (
                  <div key={roomIndex} className="mb-6 last:mb-0">
                    <div className="bg-gray-50 px-4 py-3 rounded-t-lg border-b border-gray-200 flex justify-between items-center">
                      <h3 className="text-lg font-semibold text-gray-900">Room {roomIndex + 1}</h3>
                      {room.roomType && (
                        <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                          {room.roomType}
                        </span>
                      )}
                    </div>
                    
                    {room.paxDetailsResponseDtos && (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Passenger Name</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {room.paxDetailsResponseDtos.map((pax, paxIndex) => (
                              <tr key={paxIndex} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {pax.gender || '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm font-medium text-gray-900">
                                    {`${pax.firstName || ''} ${pax.lastName || ''}`.trim() || '-'}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {pax.email || '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {pax.phoneNo || '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {pax.paxType === '1' ? 'Adult' : 
                                   pax.paxType === '2' ? `Child(A-${pax.age || ''})` : 'Other'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    pax.leadPax ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                                  }`}>
                                    {pax.leadPax ? 'Primary' : 'Secondary'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Price Summary */}
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-t-lg">
                <h2 className="text-lg font-semibold flex items-center">
                  <i className="fas fa-calculator mr-2"></i>
                  Price Summary
                </h2>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {bookingDetails.hotelPriceDetail?.platformFee && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Platform Fee</span>
                      <span className="font-semibold text-gray-900">₹{bookingDetails.hotelPriceDetail.platformFee}</span>
                    </div>
                  )}
                  {bookingDetails.hotelPriceDetail?.gst && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">GST</span>
                      <span className="font-semibold text-gray-900">₹{bookingDetails.hotelPriceDetail.gst}</span>
                    </div>
                  )}
                  {bookingDetails.hotelPriceDetail?.totalPrice && (
                    <div className="flex justify-between items-center py-2 bg-gray-50 px-4 rounded-lg">
                      <span className="text-lg font-semibold text-gray-900">Total Amount</span>
                      <span className="text-xl font-bold text-blue-600">₹{bookingDetails.hotelPriceDetail.totalPrice}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
              <div className="space-y-3">
                {(bookingDetails.status === 'BOOKED' || bookingDetails.status === 'HOLD') && (
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center justify-center"
                  >
                    <i className="fas fa-times mr-2"></i>
                    Cancel Hotel
                  </button>
                )}
                
                <button
                  onClick={handleGeneratePDF}
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center"
                >
                  <i className="fas fa-file-pdf mr-2"></i>
                  Generate PDF
                </button>

                {bookingDetails.status === 'HOLD' && (
                  <button
                    onClick={() => setShowVoucherModal(true)}
                    className="w-full bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors duration-200 flex items-center justify-center"
                  >
                    <i className="fas fa-receipt mr-2"></i>
                    Voucher
                  </button>
                )}

                {bookingDetails.priceDrop && (
                  <button
                    onClick={() => setShowRebookModal(true)}
                    className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors duration-200 flex items-center justify-center"
                  >
                    <i className="fas fa-redo mr-2"></i>
                    Re-Book
                  </button>
                )}
              </div>
            </div>

            {/* Cancellation Policy */}
            {(bookingDetails.lastCancellationDate || bookingDetails.termAndCondition) && (
              <div className="bg-white rounded-lg shadow-sm border">
                <div className="px-6 py-4 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-t-lg">
                  <h2 className="text-lg font-semibold flex items-center">
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    Cancellation Policy
                  </h2>
                </div>
                <div className="p-6">
                  {bookingDetails.lastCancellationDate && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <h4 className="font-semibold text-red-800 mb-2">Free Cancellation</h4>
                      <p className="text-red-700 text-sm">
                        Free cancellation up to {formatDate(bookingDetails.lastCancellationDate)}
                      </p>
                    </div>
                  )}
                  
                  {bookingDetails.termAndCondition && bookingDetails.termAndCondition.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="font-semibold text-gray-900">Terms & Conditions</h4>
                      <ul className="space-y-2">
                        {bookingDetails.termAndCondition.map((term, index) => (
                          <li key={index} className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                            <div dangerouslySetInnerHTML={{ __html: term }} />
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modals */}
        {/* Ticket Modal */}
        {showTicketModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Add Support Ticket</h3>
                <button
                  onClick={() => setShowTicketModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <form onSubmit={handleTicketSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Booking ID</label>
                  <input
                    type="text"
                    value={bookingId}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Query Type</label>
                  <select
                    value={ticketForm.queryType}
                    onChange={(e) => setTicketForm({...ticketForm, queryType: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="BOOKING">BOOKING</option>
                  </select>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Query</label>
                  <textarea
                    value={ticketForm.query}
                    onChange={(e) => setTicketForm({...ticketForm, query: e.target.value})}
                    rows={4}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your query"
                  />
                </div>
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
                  >
                    Submit
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowTicketModal(false)}
                    className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors duration-200"
                  >
                    Close
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Cancel Modal */}
        {showCancelModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <div className="text-center">
                <i className="fas fa-exclamation-triangle text-4xl text-red-500 mb-4"></i>
                <h3 className="text-lg font-semibold mb-2">Cancel Hotel Booking</h3>
                <p className="text-gray-600 mb-6">Are you sure you want to cancel this hotel booking? This action cannot be undone.</p>
                <div className="flex space-x-3">
                  <button
                    onClick={handleCancel}
                    className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200"
                  >
                    Yes, Cancel
                  </button>
                  <button
                    onClick={() => setShowCancelModal(false)}
                    className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors duration-200"
                  >
                    No, Keep
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Voucher Modal */}
        {showVoucherModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <div className="text-center">
                <i className="fas fa-receipt text-4xl text-yellow-500 mb-4"></i>
                <h3 className="text-lg font-semibold mb-2">Generate Voucher</h3>
                <p className="text-gray-600 mb-6">Do you want to generate a voucher for this booking?</p>
                <div className="flex space-x-3">
                  <button
                    onClick={handleVoucher}
                    className="flex-1 bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors duration-200"
                  >
                    Yes, Generate
                  </button>
                  <button
                    onClick={() => setShowVoucherModal(false)}
                    className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Rebook Modal */}
        {showRebookModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <div className="text-center">
                <i className="fas fa-redo text-4xl text-green-500 mb-4"></i>
                <h3 className="text-lg font-semibold mb-2">Re-Book Hotel</h3>
                <p className="text-gray-600 mb-6">Do you want to re-book this hotel? This will open in a new tab.</p>
                <div className="flex space-x-3">
                  <button
                    onClick={handleRebook}
                    className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors duration-200"
                  >
                    Yes, Re-Book
                  </button>
                  <button
                    onClick={() => setShowRebookModal(false)}
                    className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerBookingDetail;